import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { Notification } from '../types/notification';
import notificationService from '../services/notificationService';
import { useAuth } from './AuthContext';

// ─── State ──────────────────────────────────────────────────────────────────

export interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  paginationCursor: string | null;
  hasMore: boolean;
}

const INITIAL_STATE: NotificationState = {
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  paginationCursor: null,
  hasMore: true,
};

// ─── Actions ────────────────────────────────────────────────────────────────

type NotificationAction =
  | { type: 'FETCH_START' }
  | {
      type: 'FETCH_SUCCESS';
      payload: {
        notifications: Notification[];
        lastKey: string | undefined;
        hasMore: boolean;
        reset: boolean;
      };
    }
  | { type: 'FETCH_FAILURE' }
  | { type: 'SET_UNREAD_COUNT'; payload: number }
  | { type: 'MARK_READ_OPTIMISTIC'; payload: string[] }
  | { type: 'MARK_READ_REVERT'; payload: { ids: string[]; previousCount: number } }
  | { type: 'MARK_ALL_READ_OPTIMISTIC' }
  | { type: 'MARK_ALL_READ_REVERT'; payload: { previousNotifications: Notification[]; previousCount: number } }
  | { type: 'CLEAR_STATE' };

function notificationReducer(
  state: NotificationState,
  action: NotificationAction,
): NotificationState {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, isLoading: true };

    case 'FETCH_SUCCESS': {
      const { notifications, lastKey, hasMore, reset } = action.payload;
      return {
        ...state,
        isLoading: false,
        notifications: reset
          ? notifications
          : [...state.notifications, ...notifications],
        paginationCursor: lastKey ?? null,
        hasMore,
      };
    }

    case 'FETCH_FAILURE':
      return { ...state, isLoading: false };

    case 'SET_UNREAD_COUNT':
      return { ...state, unreadCount: action.payload };

    case 'MARK_READ_OPTIMISTIC': {
      const idsSet = new Set(action.payload);
      // Count how many of the targeted IDs are currently unread
      let decrementBy = 0;
      const updatedNotifications = state.notifications.map((n) => {
        if (idsSet.has(n.id) && !n.read) {
          decrementBy += 1;
          return { ...n, read: true };
        }
        return n;
      });
      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount: Math.max(0, state.unreadCount - decrementBy),
      };
    }

    case 'MARK_READ_REVERT': {
      const revertSet = new Set(action.payload.ids);
      return {
        ...state,
        notifications: state.notifications.map((n) =>
          revertSet.has(n.id) ? { ...n, read: false } : n,
        ),
        unreadCount: action.payload.previousCount,
      };
    }

    case 'MARK_ALL_READ_OPTIMISTIC':
      return {
        ...state,
        notifications: state.notifications.map((n) => ({ ...n, read: true })),
        unreadCount: 0,
      };

    case 'MARK_ALL_READ_REVERT':
      return {
        ...state,
        notifications: action.payload.previousNotifications,
        unreadCount: action.payload.previousCount,
      };

    case 'CLEAR_STATE':
      return { ...INITIAL_STATE };

    default:
      return state;
  }
}

// ─── Context ────────────────────────────────────────────────────────────────

export interface NotificationActions {
  fetchNotifications: (reset?: boolean) => Promise<void>;
  markAsRead: (ids: string[]) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refreshUnreadCount: () => Promise<void>;
  clearState: () => void;
}

type NotificationContextValue = NotificationState & NotificationActions;

const NotificationContext = createContext<NotificationContextValue | undefined>(
  undefined,
);

// ─── Polling constants ──────────────────────────────────────────────────────

const BASE_POLL_INTERVAL_MS = 30_000; // 30 seconds
const MAX_POLL_INTERVAL_MS = 300_000; // 5 minutes (300 seconds)

/**
 * Compute the next poll interval with exponential backoff.
 *
 * Formula: min(30 * 2^(failureCount - 1), 300) seconds
 * On success (failureCount === 0) → 30 seconds.
 */
export function computePollInterval(failureCount: number): number {
  if (failureCount <= 0) return BASE_POLL_INTERVAL_MS;
  const intervalMs = BASE_POLL_INTERVAL_MS * Math.pow(2, failureCount - 1);
  return Math.min(intervalMs, MAX_POLL_INTERVAL_MS);
}

// ─── Provider ───────────────────────────────────────────────────────────────

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, user } = useAuth();
  const [state, dispatch] = useReducer(notificationReducer, INITIAL_STATE);

  // Refs for polling lifecycle
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const failureCountRef = useRef(0);
  const isVisibleRef = useRef(!document.hidden);
  const isMountedRef = useRef(true);

  // ── Actions ─────────────────────────────────────────────────────────────

  const refreshUnreadCount = useCallback(async () => {
    try {
      const { count } = await notificationService.getUnreadCount();
      if (isMountedRef.current) {
        dispatch({ type: 'SET_UNREAD_COUNT', payload: count });
        // Reset backoff on success
        failureCountRef.current = 0;
      }
    } catch (error) {
      console.error('NotificationContext: Failed to refresh unread count', error);
      if (isMountedRef.current) {
        failureCountRef.current += 1;
      }
    }
  }, []);

  const fetchNotifications = useCallback(
    async (reset = false) => {
      dispatch({ type: 'FETCH_START' });
      try {
        const cursor = reset ? undefined : state.paginationCursor ?? undefined;
        const response = await notificationService.getNotifications(
          20,
          cursor,
        );
        if (isMountedRef.current) {
          dispatch({
            type: 'FETCH_SUCCESS',
            payload: {
              notifications: response.notifications,
              lastKey: response.lastKey,
              hasMore: response.hasMore,
              reset,
            },
          });
        }
      } catch (error) {
        console.error('NotificationContext: Failed to fetch notifications', error);
        if (isMountedRef.current) {
          dispatch({ type: 'FETCH_FAILURE' });
        }
      }
    },
    [state.paginationCursor],
  );

  const markAsRead = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0) return;

      const previousCount = state.unreadCount;
      // Optimistic update
      dispatch({ type: 'MARK_READ_OPTIMISTIC', payload: ids });

      try {
        await notificationService.markAsRead(ids);
      } catch (error) {
        console.error('NotificationContext: Failed to mark as read, reverting', error);
        if (isMountedRef.current) {
          dispatch({
            type: 'MARK_READ_REVERT',
            payload: { ids, previousCount },
          });
        }
      }
    },
    [state.unreadCount],
  );

  const markAllAsRead = useCallback(async () => {
    const previousNotifications = state.notifications;
    const previousCount = state.unreadCount;
    // Optimistic update
    dispatch({ type: 'MARK_ALL_READ_OPTIMISTIC' });

    try {
      await notificationService.markAllAsRead();
    } catch (error) {
      console.error('NotificationContext: Failed to mark all as read, reverting', error);
      if (isMountedRef.current) {
        dispatch({
          type: 'MARK_ALL_READ_REVERT',
          payload: { previousNotifications, previousCount },
        });
      }
    }
  }, [state.notifications, state.unreadCount]);

  const clearState = useCallback(() => {
    dispatch({ type: 'CLEAR_STATE' });
  }, []);

  // ── Polling lifecycle ───────────────────────────────────────────────────

  const scheduleNextPoll = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }

    const intervalMs = computePollInterval(failureCountRef.current);

    pollTimerRef.current = setTimeout(async () => {
      if (!isMountedRef.current) return;
      if (!isVisibleRef.current) return; // Don't poll while hidden

      await refreshUnreadCount();

      // Schedule the next poll (interval may have changed due to backoff)
      if (isMountedRef.current && isVisibleRef.current) {
        scheduleNextPoll();
      }
    }, intervalMs);
  }, [refreshUnreadCount]);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    scheduleNextPoll();
  }, [stopPolling, scheduleNextPoll]);

  // ── Visibility change handler ───────────────────────────────────────────

  useEffect(() => {
    const handleVisibilityChange = () => {
      const visible = !document.hidden;
      isVisibleRef.current = visible;

      if (visible && isAuthenticated) {
        // Tab became visible: immediate poll + resume interval
        refreshUnreadCount();
        startPolling();
      } else {
        // Tab hidden: pause polling
        stopPolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, refreshUnreadCount, startPolling, stopPolling]);

  // ── Auth state changes ────────────────────────────────────────────────

  useEffect(() => {
    if (isAuthenticated && user) {
      // User just authenticated — fetch initial unread count and start polling
      failureCountRef.current = 0;
      refreshUnreadCount();
      startPolling();
    } else {
      // User logged out or not authenticated — clear everything
      stopPolling();
      failureCountRef.current = 0;
      dispatch({ type: 'CLEAR_STATE' });
    }
  }, [isAuthenticated, user, refreshUnreadCount, startPolling, stopPolling]);

  // ── Cleanup on unmount ────────────────────────────────────────────────

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  // ── Context value ─────────────────────────────────────────────────────

  const value = useMemo<NotificationContextValue>(
    () => ({
      notifications: state.notifications,
      unreadCount: state.unreadCount,
      isLoading: state.isLoading,
      paginationCursor: state.paginationCursor,
      hasMore: state.hasMore,
      fetchNotifications,
      markAsRead,
      markAllAsRead,
      refreshUnreadCount,
      clearState,
    }),
    [
      state.notifications,
      state.unreadCount,
      state.isLoading,
      state.paginationCursor,
      state.hasMore,
      fetchNotifications,
      markAsRead,
      markAllAsRead,
      refreshUnreadCount,
      clearState,
    ],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// ─── Hook ───────────────────────────────────────────────────────────────────

export const useNotifications = (): NotificationContextValue => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      'useNotifications must be used within a NotificationProvider',
    );
  }
  return context;
};
